import { useEffect, useRef, useState } from "react";
import { CookiesProvider } from "react-cookie";
import { loadableReady } from "@loadable/component";
import { hydrateRoot } from "react-dom/client";
import { ApolloProvider } from "@apollo/client/react";
import { BrowserRouter } from "react-router";
import { AppConfig, AppConfigProvider, useIsMounted } from "@ct-react/core";
import { defineLocale, LocaleProvider, useLocaleContext } from "@ct-react/locale";
import buildApolloClient from "@shared/graphql";
import { handleLocaleChange } from "@shared/locale";
import { APOLLO_CACHE_SCRIPT_ID, APP_CTX_SCRIPT_ID } from "@shared/constants";
import App from "./app/app";
import { HeadProvider } from "./app/components/layout/head";
import { createHeadManager } from "./app/components/layout/head-manager";
import { HttpStatusProvider } from "./app/contexts/http-status";
import { createHttpStatusManager } from "./app/contexts/http-status-manager";

const loadingLocale = defineLocale(navigator.languages || [], window.location.pathname);
const { manager: headManager } = createHeadManager();
const httpStatusManager = createHttpStatusManager();

const Wrapper = () => {

  const isMounted = useIsMounted();
  const { locale } = useLocaleContext();

  const ctx = useRef<AppConfig>(window.__APP_CTX__);
  const [ apollo, setApollo ] = useState(buildApolloClient(ctx.current, false, locale, window.__APOLLO_STATE__));

  useEffect(() => {
    if (!isMounted) return;
    setApollo(buildApolloClient(ctx.current, false, locale));
  }, [ locale ]);

  useEffect(() => {
    if (isMounted) return;
    delete (window as Partial<Window>).__APP_CTX__;
    const stateScriptTag = document.getElementById(APP_CTX_SCRIPT_ID);
    !!stateScriptTag && stateScriptTag.parentNode?.removeChild(stateScriptTag);
  }, [ ctx ]);

  useEffect(() => {
    if (isMounted) return;
    delete (window as Partial<Window>).__APOLLO_STATE__;
    const stateScriptTag = document.getElementById(APOLLO_CACHE_SCRIPT_ID);
    !!stateScriptTag && stateScriptTag.parentNode?.removeChild(stateScriptTag);
  }, [ apollo ]);

  return (
    <CookiesProvider>
      <ApolloProvider client={apollo}>
        <AppConfigProvider value={ctx.current}>
          <HeadProvider manager={headManager}>
            <HttpStatusProvider manager={httpStatusManager}>
              <BrowserRouter>
                <App />
              </BrowserRouter>
            </HttpStatusProvider>
          </HeadProvider>
        </AppConfigProvider>
      </ApolloProvider>
    </CookiesProvider>);

}

loadableReady()
  .then(() => navigator.serviceWorker.register("/public/sw.js"))
  .then(() => handleLocaleChange(loadingLocale))
  .then(() => hydrateRoot(
    document.getElementById("app")!,
    <LocaleProvider locale={loadingLocale}
                    beforeLocaleChange={locale => handleLocaleChange(locale)}>
      <Wrapper />
    </LocaleProvider>));
